import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const DiscountsReport = ({ selectedLocation }) => {
  const [discountsReport, setDiscountsReport] = useState({
    uzycia: [],
    total: 0,
    page: 1,
    per_page: 50,
    total_pages: 1,
    summary: {}
  });
  const [allDiscountsData, setAllDiscountsData] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [selectedDiscountIds, setSelectedDiscountIds] = useState([]);
  const [discountsDateRange, setDiscountsDateRange] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadAvailableDiscounts();
  }, []);

  // Reload raportu przy zmianie lokalizacji (admin moze przelaczac sklepy)
  useEffect(() => {
    loadDiscountsReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  const loadAvailableDiscounts = async () => {
    try {
      const response = await api.get('/rabaty/lista');
      const data = response.data;
      if (data.success) {
        setAvailableDiscounts(data.data.rabaty || []);
      }
    } catch (err) {
      console.error('Blad podczas pobierania listy rabatow:', err);
    }
  };

  // Zaokraglenie do grosza (spojnosc z groszowym modelem) - rabaty_uzycie
  // moze przechowywac >2 miejsca, wiec suma z surowych wartosci rozjezdza sie
  // z suma wyswietlanych (toFixed(2)) wierszy. Zaokraglamy u zrodla, zeby
  // podsumowanie i wiersze zgadzaly sie do grosza.
  const roundGrosz = (val) => Math.round((parseFloat(val) || 0) * 100) / 100;

  const applyDiscountFilter = (data, selectedIds) => {
    let filteredData = data;

    if (selectedIds.length > 0) {
      filteredData = data.filter(r => selectedIds.includes(r.rabat_id));
    }

    const suma_rabatow = filteredData.reduce((sum, r) => sum + parseFloat(r.kwota_rabatu || 0), 0);
    const liczba_uzyc = filteredData.length;

    setDiscountsReport({
      uzycia: filteredData,
      total: liczba_uzyc,
      page: 1,
      per_page: 999,
      total_pages: 1,
      summary: {
        liczba_uzyc: liczba_uzyc,
        suma_rabatow: suma_rabatow,
        sredni_rabat: liczba_uzyc > 0 ? suma_rabatow / liczba_uzyc : 0,
        min_rabat: liczba_uzyc > 0 ? Math.min(...filteredData.map(r => parseFloat(r.kwota_rabatu || 0))) : 0,
        max_rabat: liczba_uzyc > 0 ? Math.max(...filteredData.map(r => parseFloat(r.kwota_rabatu || 0))) : 0
      }
    });
  };

  const loadDiscountsReport = async (page = 1) => {
    try {
      setDiscountsLoading(true);
      const params = new URLSearchParams({
        data_od: discountsDateRange.date_from,
        data_do: discountsDateRange.date_to
      });
      // Filtr po lokalizacji - bez tego widac rabaty z innych sklepow
      if (selectedLocation?.id) {
        params.append('location_id', selectedLocation.id);
      }
      // Filtr po rabacie (jesli ustawiony) - spojnosc z PDF
      if (selectedDiscountIds.length > 0) {
        params.append('rabat_ids', selectedDiscountIds.join(','));
      }

      const response = await api.get(`/rabaty/raporty/dzienne?${params}`);
      const data = response.data;

      if (data.success) {
        const raporty = data.data?.raporty || data.message?.raporty || [];

        const mappedUzycia = raporty.map(r => ({
          id: r.id,
          rabat_id: r.rabat_id,
          transakcja_id: r.transakcja_id,
          kasjer: r.kasjer,
          kwota_przed_rabatem: roundGrosz(r.suma_przed_rabatem),
          kwota_rabatu: roundGrosz(r.suma_rabatow),
          kwota_po_rabacie: roundGrosz(r.suma_po_rabacie),
          data_zastosowania: r.dzien,
          dzien: r.dzien,
          godzina: r.godzina,
          rabat_nazwa: r.rabat_nazwa,
          typ_rabatu: r.typ_rabatu,
          rabat_wartosc: r.rabat_wartosc,
          numer_paragonu: r.numer_paragonu,
          numer_transakcji: r.transakcja_id,
          kod_weryfikacyjny: r.kod_weryfikacyjny,
          numer_karty_klienta: r.numer_karty_klienta,
          nazwa_influencera: r.nazwa_influencera
        }));

        setAllDiscountsData(mappedUzycia);
        applyDiscountFilter(mappedUzycia, selectedDiscountIds);
      }
    } catch (err) {
      console.error('Blad podczas pobierania raportu rabatow:', err);
    } finally {
      setDiscountsLoading(false);
    }
  };

  const fetchDiscountsPdf = async (action = 'download') => {
    try {
      const params = new URLSearchParams({
        data_od: discountsDateRange.date_from,
        data_do: discountsDateRange.date_to
      });
      if (selectedLocation?.id) {
        params.append('location_id', selectedLocation.id);
      }
      // Pass selected discount IDs for filtering
      if (selectedDiscountIds.length > 0) {
        params.append('rabat_ids', selectedDiscountIds.join(','));
      }

      const response = await api.get(`/rabaty/raporty/dzienne/pdf?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => printWindow.print();
        }
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `raport_rabatow_${discountsDateRange.date_from}_${discountsDateRange.date_to}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Blad generowania PDF:', err);
      toast.error('Wystapil blad podczas generowania PDF');
    }
  };

  const printDiscountsReport = () => fetchDiscountsPdf('print');
  const downloadDiscountsPdf = () => fetchDiscountsPdf('download');

  const summary = discountsReport.summary || {};
  const uzycia = discountsReport.uzycia || [];

  return (
    <div>
      {/* Filtry - jeden wiersz */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        alignItems: 'center',
        padding: '0.75rem',
        backgroundColor: 'var(--warning-light)',
        borderRadius: '0.375rem',
        marginBottom: '0.75rem',
        border: '1px solid var(--warning)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Od:</label>
          <input
            type="date"
            value={discountsDateRange.date_from}
            onChange={(e) => setDiscountsDateRange(prev => ({ ...prev, date_from: e.target.value }))}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Do:</label>
          <input
            type="date"
            value={discountsDateRange.date_to}
            onChange={(e) => setDiscountsDateRange(prev => ({ ...prev, date_to: e.target.value }))}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Rabat:</label>
          <select
            value={selectedDiscountIds.length === 1 ? selectedDiscountIds[0] : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setSelectedDiscountIds([]);
                if (allDiscountsData.length > 0) {
                  applyDiscountFilter(allDiscountsData, []);
                }
              } else {
                const newIds = [parseInt(val)];
                setSelectedDiscountIds(newIds);
                if (allDiscountsData.length > 0) {
                  applyDiscountFilter(allDiscountsData, newIds);
                }
              }
            }}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', minWidth: '180px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          >
            <option value="">Wszystkie</option>
            {availableDiscounts.map(discount => (
              <option key={discount.id} value={discount.id}>
                {discount.nazwa} ({discount.typ_rabatu === 'procentowy' ? discount.wartosc + '%' : discount.wartosc + 'zł'})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => loadDiscountsReport(1)}
          disabled={discountsLoading}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--orange)', color: 'var(--orange)',
            fontSize: '12px',
            fontWeight: '600'
          }}
        >
          {discountsLoading ? '⟳...' : '🔍 Szukaj'}
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={printDiscountsReport}
            disabled={uzycia.length === 0}
            style={{
              padding: '6px 12px',
              border: `1px solid ${uzycia.length === 0 ? 'var(--border)' : 'var(--text-muted)'}`,
              color: uzycia.length === 0 ? 'var(--text-muted)' : 'var(--text-secondary)',
              fontSize: '12px',
              opacity: uzycia.length === 0 ? 0.7 : 1
            }}
          >
            🖨️ Drukuj
          </button>
          <button
            onClick={downloadDiscountsPdf}
            disabled={uzycia.length === 0}
            style={{
              padding: '6px 12px',
              border: `1px solid ${uzycia.length === 0 ? 'var(--border)' : 'var(--success)'}`,
              color: 'var(--success)',
              fontSize: '12px',
              opacity: uzycia.length === 0 ? 0.7 : 1
            }}
          >
            📥 PDF
          </button>
        </div>
      </div>

      {/* Podsumowanie */}
      {summary.liczba_uzyc > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
          <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
            <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📊 Podsumowanie</h5>
          </div>
          <div style={{ padding: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
              <div style={{ backgroundColor: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: '0.375rem', padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--warning)' }}>{summary.liczba_uzyc || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--warning)' }}>Liczba użyć</div>
              </div>
              <div style={{ backgroundColor: 'var(--orange-light)', border: '1px solid var(--orange)', borderRadius: '0.375rem', padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--danger)' }}>{(summary.suma_rabatow || 0).toFixed(2)} zł</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>Suma rabatów</div>
              </div>
              <div style={{ backgroundColor: 'var(--info-light)', border: '1px solid var(--info)', borderRadius: '0.375rem', padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--info)' }}>{(summary.sredni_rabat || 0).toFixed(2)} zł</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--info)' }}>Średni rabat</div>
              </div>
              <div style={{ backgroundColor: 'var(--purple-light)', border: '1px solid var(--purple)', borderRadius: '0.375rem', padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--purple)' }}>{(summary.min_rabat || 0).toFixed(2)} - {(summary.max_rabat || 0).toFixed(2)} zł</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--purple)' }}>Min - Max</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela użyć */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)' }}>
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📋 Lista użytych rabatów ({discountsReport.total || 0})</h5>
          {discountsReport.total_pages > 1 && (
            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
              <button
                onClick={() => loadDiscountsReport(discountsReport.page - 1)}
                disabled={discountsReport.page <= 1}
                style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', backgroundColor: discountsReport.page <= 1 ? 'var(--border)' : 'var(--card-bg)' }}
              >
                ← Poprzednia
              </button>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Strona {discountsReport.page} z {discountsReport.total_pages}
              </span>
              <button
                onClick={() => loadDiscountsReport(discountsReport.page + 1)}
                disabled={discountsReport.page >= discountsReport.total_pages}
                style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', backgroundColor: discountsReport.page >= discountsReport.total_pages ? 'var(--border)' : 'var(--card-bg)' }}
              >
                Następna →
              </button>
            </div>
          )}
        </div>
        <div style={{ padding: '0.5rem', overflowX: 'auto' }}>
          {uzycia.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Godzina</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nr dokumentu</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nazwa rabatu</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Typ</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota przed</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Rabat</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota po</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Kasjer</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Kod weryfikacyjny</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Nr karty klienta</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Influencer</th>
                </tr>
              </thead>
              <tbody>
                {uzycia.map((u, idx) => (
                  <tr key={u.id || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.4rem' }}>
                      {u.data_zastosowania ? new Date(u.data_zastosowania).toLocaleDateString('pl-PL') : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {u.godzina ? u.godzina.substring(0, 5) : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {u.numer_paragonu || u.numer_transakcji || `#${u.transakcja_id}`}
                    </td>
                    <td style={{ padding: '0.4rem', fontWeight: '500' }}>{u.rabat_nazwa || '-'}</td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.15rem 0.4rem',
                        backgroundColor: u.typ_rabatu === 'procentowy' ? 'var(--info-light)' : 'var(--orange-light)',
                        color: u.typ_rabatu === 'procentowy' ? 'var(--info)' : 'var(--orange)',
                        borderRadius: '10px',
                        fontSize: '0.65rem'
                      }}>
                        {u.typ_rabatu === 'procentowy' ? `${u.rabat_wartosc}%` : `${u.rabat_wartosc} zł`}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'right' }}>{(u.kwota_przed_rabatem || 0).toFixed(2)} zł</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right', color: 'var(--orange)', fontWeight: '600' }}>-{(u.kwota_rabatu || 0).toFixed(2)} zł</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>{(u.kwota_po_rabacie || 0).toFixed(2)} zł</td>
                    <td style={{ padding: '0.4rem' }}>{u.kasjer || '-'}</td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      {u.kod_weryfikacyjny ? (
                        <span style={{ padding: '0.15rem 0.4rem', backgroundColor: 'var(--warning-light)', color: 'var(--warning)', borderRadius: '4px', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                          {u.kod_weryfikacyjny}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      {u.numer_karty_klienta ? (
                        <span style={{ padding: '0.15rem 0.4rem', backgroundColor: 'var(--success-light)', color: 'var(--success)', borderRadius: '4px', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                          {u.numer_karty_klienta}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      {u.nazwa_influencera ? (
                        <span style={{ padding: '0.15rem 0.4rem', backgroundColor: 'var(--purple-light)', color: 'var(--purple)', borderRadius: '4px', fontSize: '0.65rem' }}>
                          {u.nazwa_influencera}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
              {discountsLoading ? 'Ładowanie...' : 'Kliknij "Szukaj" aby załadować raport rabatów'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountsReport;
